import {
  Component,
  OnInit,
  Injectable,
  Input,
  Output,
  EventEmitter,
} from "@angular/core";
import { SelectionModel } from "@angular/cdk/collections";
import { FlatTreeControl } from "@angular/cdk/tree";
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from "@angular/material/tree";
import { BehaviorSubject } from "rxjs";
import { identifierModuleUrl } from "@angular/compiler";
import { ToastrAlertService } from "src/app/core/services/toastrAlert.service";

/**
 * Node for to-do item
 */
export class TodoItemNode {
  children: TodoItemNode[];
  item: string;
}

/** Flat to-do item node with expandable and level information */
export class TodoItemFlatNode {
  item: string;
  level: number;
  expandable: boolean;
}

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable()
export class ChecklistDatabase {
  dataChange = new BehaviorSubject<TodoItemNode[]>([]);

  get data(): TodoItemNode[] {
    return this.dataChange.value;
  }

  constructor() {}

  initialize(treeData) {
    // Build the tree nodes from Json object. The result is a list of `TodoItemNode` with nested
    //     file node as children.
    const data = this.buildFileTree(treeData, 0);

    // Notify the change.
    this.dataChange.next(data);
  }

  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `TodoItemNode`.
   */
  buildFileTree(obj: { [key: string]: any }, level: number): TodoItemNode[] {
    return Object.keys(obj).reduce<TodoItemNode[]>((accumulator, key) => {
      const value = obj[key];
      const node = new TodoItemNode();
      node.item = key;

      if (value != null) {
        if (typeof value === "object") {
          node.children = this.buildFileTree(value, level + 1);
        } else {
          node.item = value;
        }
      }

      return accumulator.concat(node);
    }, []);
  }

  /** Add an item to to-do list */
  insertItem(parent: TodoItemNode, name: string) {
    if (parent.children) {
      parent.children.push({ item: name } as TodoItemNode);
      this.dataChange.next(this.data);
    }
  }

  updateItem(node: TodoItemNode, name: string) {
    node.item = name;
    this.dataChange.next(this.data);
  }
}

/**
 * @title Tree with checkboxes
 */

@Component({
  selector: "app-tree",
  templateUrl: "./tree.component.html",
  styleUrls: ["./tree.component.css"],
  providers: [ChecklistDatabase],
})
export class TreeComponent implements OnInit {
  @Input() treeData;
  @Input() options;

  @Output() itemSelected = new EventEmitter<any>();
  @Output() itemsSelected = new EventEmitter<any>();
  @Output() action = new EventEmitter<any>();
  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<TodoItemFlatNode, TodoItemNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<TodoItemNode, TodoItemFlatNode>();

  /** A selected parent node to be inserted */
  selectedParent: TodoItemFlatNode | null = null;

  /** The new item's name */
  newItemName = "";

  treeControl: FlatTreeControl<TodoItemFlatNode>;

  treeFlattener: MatTreeFlattener<TodoItemNode, TodoItemFlatNode>;

  dataSource: MatTreeFlatDataSource<TodoItemNode, TodoItemFlatNode>;

  isRESelected: Boolean = false;

  isOCSelected: Boolean = false;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<TodoItemFlatNode>(
    true /* multiple */
  );

  constructor(
    private _database: ChecklistDatabase,
    private toastrAlertService: ToastrAlertService
  ) {
    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren
    );
    this.treeControl = new FlatTreeControl<TodoItemFlatNode>(
      this.getLevel,
      this.isExpandable
    );
    this.dataSource = new MatTreeFlatDataSource(
      this.treeControl,
      this.treeFlattener
    );
  }

  ngOnInit(): void {
    this._database.initialize(this.treeData);
    this._database.dataChange.subscribe((data) => {
      this.dataSource.data = data;
    });
  }

  getLevel = (node: TodoItemFlatNode) => node.level;

  isExpandable = (node: TodoItemFlatNode) => node.expandable;

  getChildren = (node: TodoItemNode): TodoItemNode[] => node.children;

  hasChild = (_: number, _nodeData: TodoItemFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: TodoItemFlatNode) =>
    _nodeData.item === "";

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: TodoItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode.item === node.item
        ? existingNode
        : new TodoItemFlatNode();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.expandable = !!node.children?.length;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  /** Whether all the descendants of the node are selected. */
  descendantsAllSelected(node: TodoItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    return descAllSelected;
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: TodoItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) =>
      this.checklistSelection.isSelected(child)
    );
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  todoItemSelectionToggle(node: TodoItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);

    // Force update for the parent
    descendants.forEach((child) => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);
  }

  /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
  todoLeafItemSelectionToggle(
    node: TodoItemFlatNode,
    element: HTMLInputElement
  ): void {
    const nodeSelected = this.isNodeSelected(node);
    const counter = this.checklistSelection.selected.length;
    let parent = this.getParentNode(node);
    if (this.getLevel(parent) > 0) {
      parent = this.getParentNode(parent);
    }

    if (!nodeSelected) {
      if (counter == 0) {
        parent.item.startsWith("Reforma")
          ? (this.isRESelected = true)
          : (this.isOCSelected = true);
      }

      if (
        counter == 1 &&
        this.isOCSelected &&
        parent.item.startsWith("Otros")
      ) {
        this.toastrAlertService.toastrWarning(
          "Otros certificados: solo se admite 1 tipo de certificado a la vez."
        );
        element.checked = false;
      } else if (
        counter == 1 &&
        this.isOCSelected &&
        parent.item.startsWith("Reforma")
      ) {
        this.toastrAlertService.toastrWarning(
          "No se puede combinar Otros Certificados con Reformas de importancia en la misma solicitud."
        );
        element.checked = false;
      } else if (
        counter >= 1 &&
        this.isRESelected &&
        parent.item.startsWith("Otros")
      ) {
        this.toastrAlertService.toastrWarning(
          "No se puede combinar Reformas de importancia con Otros Certificados en la misma solicitud."
        );
        element.checked = false;
      } else if (
        counter == 4 &&
        this.isRESelected &&
        parent.item.startsWith("Reforma")
      ) {
        this.toastrAlertService.toastrWarning(
          "Reforma de importancia: no se admiten más de 4 reformas simultáneamente,\npara añadir más reformas cree una nueva solicitud."
        );
        element.checked = false;
      } else {
        this.checklistSelection.toggle(node);
      }
    } else {
      counter == 1 && parent.item.startsWith("Reformas")
        ? (this.isRESelected = false)
        : (this.isOCSelected = false);
      this.checklistSelection.toggle(node);
    }
  }

  /* Checks all the parents when a leaf node is selected/unselected */
  checkAllParentsSelection(node: TodoItemFlatNode): void {
    let parent: TodoItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  /** Check root node checked state and change it accordingly */
  checkRootNodeSelection(node: TodoItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  /* Return if item is checked */
  isNodeSelected(node: TodoItemFlatNode): boolean {
    return this.checklistSelection.isSelected(node);
  }

  /* Senmd selection to parent */
  sendSelected() {
    if (this.checklistSelection.selected.length == 0) {
      this.toastrAlertService.toastrFail(
        "Debe seleccionar al menos 1 tipo de certificado."
      );
    } else {
      this.treeControl.collapseAll();
      if (this.isOCSelected) {
        let child = this.checklistSelection.selected[0];
        let parent = this.getParentNode(child);
        this.itemSelected.emit({ child: child.item, root: parent });
      } else {
        let child = "";
        let selectionGroup = "";
        let parent = this.getParentNode(
          this.getParentNode(this.checklistSelection.selected[0])
        );
        this.checklistSelection.selected.forEach((element) => {
          child += element.item?.split("-")[0].trim() + "__";
          selectionGroup += element.item?.trim() + " | ";
        });
        child = child.substring(0, child.length - 2);
        selectionGroup = selectionGroup.substring(0, selectionGroup.length - 3);
        this.itemsSelected.emit({
          child: child,
          sGroup: selectionGroup,
          root: parent,
        });
      }
    }
  }

  /* Get the parent node of a node */
  getParentNode(node: TodoItemFlatNode): TodoItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  /** Select the category so we can insert the new item. */
  addNewItem(node: TodoItemFlatNode) {
    const parentNode = this.flatNodeMap.get(node);
    this._database.insertItem(parentNode!, "");
    this.treeControl.expand(node);
  }

  updateItem(form: any, node: TodoItemFlatNode) {
    const parentNode = this.flatNodeMap.get(node);
    this._database.insertItem(parentNode!, "");
    this.treeControl.expand(node);
  }

  /** Save the node to database */
  saveNode(node: TodoItemFlatNode, itemValue: string) {
    const nestedNode = this.flatNodeMap.get(node);
    this._database.updateItem(nestedNode!, itemValue);
  }

  clickElement(node: TodoItemFlatNode) {
    let child = node.item;
    this.treeControl.collapseAll();
    let parent = this.getParentNode(node);
    if (this.getLevel(parent) > 0) {
      parent = this.getParentNode(parent);
    }
    this.itemSelected.emit({ child: child, root: parent });
  }

  addReform(node: TodoItemFlatNode) {
    this.action.emit({ type: "createReform", value: node });
  }

  createChildReform(node: TodoItemFlatNode) {
    let parent = this.getParentNode(node);
    this.action.emit({
      type: "createReformChild",
      value: node,
      parent: parent,
    });
  }

  updateReform(node: TodoItemFlatNode) {
    this.action.emit({ type: "updateReform", value: node });
  }

  updateChild(node: TodoItemFlatNode) {
    let parent = this.getParentNode(node);
    if (node.level == 1) {
      if (parent.item == "Otros certificados") {
        this.action.emit({ type: "updateOtherCertificate", value: node });
      } else {
        this.action.emit({ type: "updateReform", value: node });
      }
    } else if (node.level == 2) {
      this.action.emit({
        type: "updateReformChild",
        value: node,
        parent: parent,
      });
    }
  }

  createOtherCertificate(node: TodoItemFlatNode) {
    this.action.emit({ type: "createOtherCertificate", value: node });
  }

  removeChild(node: TodoItemFlatNode) {
    if (node.level == 1) {
      let parent = this.getParentNode(node);
      if (parent.item == "Otros certificados") {
        this.action.emit({ type: "removeOtherCertificate", value: node });
      } else {
        this.action.emit({ type: "removeReform", value: node });
      }
    } else if (node.level == 2) {
      this.action.emit({ type: "removeReform", value: node });
    }
  }

  isOtherCertificate(node: TodoItemFlatNode): boolean {
    if (node) {
      if (this.getLevel(node) == 0 && node.item == "Otros certificados") {
        return true;
      } else if (this.getLevel(node) > 0) {
        let parent = this.getParentNode(node);
        if (this.getLevel(parent) == 0 && parent.item == "Otros certificados") {
          return true;
        }
      }
    }
    return false;
  }
  /*
  isOtherCertificate(node: TodoItemFlatNode): boolean {
    if (node) {
      if (this.getLevel(node) == 0 && node.item == "Otros certificados") {
        return true;
      } else if (
        this.getLevel(node) == 0 &&
        node.item != "Otros certificados"
      ) {
        return false;
      } else if (this.getLevel(node) > 0) {
        let parent = this.getParentNode(node);
        if (this.getLevel(parent) == 0 && parent.item == "Otros certificados") {
          return false;
        }
      }
    }

    return true;
  }
  */
}
